import { IconCheckDefault } from '@iwoca/icons';
import { Text } from '@iwoca/orion-ui';
import { useLocation } from 'react-router-dom';

import { ONBOARDING_PATHS } from '../../../constants';

enum OnboardingSteps {
    APPLY = 'apply',
    CHECKS = 'checks',
    SIGN_AND_ACTIVATE = 'sign_and_activate'
}

enum OnboardingStepStatus {
    COMPLETED = 'COMPLETED',
    CURRENT = 'CURRENT',
    UPCOMING = 'UPCOMING'
}

type ProgressStep = {
    enum: OnboardingSteps;
    label: string;
    status: OnboardingStepStatus;
};

const progressSteps: ProgressStep[] = [
    {
        label: 'Apply',
        status: OnboardingStepStatus.COMPLETED,
        enum: OnboardingSteps.APPLY
    },
    {
        label: 'Complete checks',
        status: OnboardingStepStatus.CURRENT,
        enum: OnboardingSteps.CHECKS
    },
    {
        label: 'Sign and activate',
        status: OnboardingStepStatus.UPCOMING,
        enum: OnboardingSteps.SIGN_AND_ACTIVATE
    }
];

export const OnboardingProgressBar = () => {
    const { pathname } = useLocation();

    const getActiveStepFromPathname = () => {
        if (pathname.includes(ONBOARDING_PATHS.ONBOARDING_CHECKS)) {
            return OnboardingSteps.CHECKS;
        }
        if (pathname.includes(ONBOARDING_PATHS.ONBOARDING_DOCUMENTS)) {
            return OnboardingSteps.SIGN_AND_ACTIVATE;
        }
        return OnboardingSteps.APPLY;
    };

    const activeIndex = progressSteps.findIndex(
        (s) => s.enum === getActiveStepFromPathname()
    );

    const steps = progressSteps.map((step, index) => {
        // Apply step is always completed
        if (step.enum === OnboardingSteps.APPLY) {
            return { ...step, status: OnboardingStepStatus.COMPLETED };
        }

        if (index === activeIndex) {
            return { ...step, status: OnboardingStepStatus.CURRENT };
        }

        if (index < activeIndex) {
            return { ...step, status: OnboardingStepStatus.COMPLETED };
        }

        return { ...step, status: OnboardingStepStatus.UPCOMING };
    });

    return (
        <div className="w-full">
            <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                    <div key={step.enum} className="flex flex-1 items-center">
                        <div
                            className={`flex items-center gap-xs pt-s ${
                                step.status === OnboardingStepStatus.COMPLETED
                                    ? 'rounded-sm w-full border-t-2 border-secondary-40'
                                    : step.status ===
                                        OnboardingStepStatus.CURRENT
                                      ? 'rounded-sm w-full border-t-2 border-primary-10'
                                      : 'rounded-sm w-full border-t-2 border-secondary-90'
                            }`}
                        >
                            <div>
                                {step.status ===
                                    OnboardingStepStatus.COMPLETED && (
                                    <IconCheckDefault className="h-4 w-4 fill-secondary-40 text-white" />
                                )}
                            </div>

                            <Text
                                className={`text-s font-med ${
                                    step.status === OnboardingStepStatus.CURRENT
                                        ? 'text-primary-10'
                                        : 'text-secondary-40'
                                }`}
                            >
                                {step.label}
                            </Text>
                        </div>

                        {index < steps.length - 1 && (
                            <div className="mx-xs flex-1">
                                <div
                                    className={`h-0.5 ${
                                        step.status ===
                                        OnboardingStepStatus.COMPLETED
                                            ? 'bg-primary-40'
                                            : 'bg-secondary-80'
                                    }`}
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
